import React, { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Charge, Plan } from './types/planTypes';
import { X, Search, AlertCircle, Check } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAppSelector } from '@/hooks/useAppSelector';
import { selectAllPricingMetrics } from '@/store/selectors/metricsSelectors';
import { Checkbox } from '@/components/ui/checkbox';
import { addChargeMutation, ChargeInput } from '@/graphql/mutations/addCharge';
import { supabase } from '@/integrations/supabase/client';
// Type assertion to allow access to tables not defined in the database types
const supabaseClient = supabase as any;

interface AddChargeModalProps {
  plan: Plan;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (newCharge: Charge) => void;
  quarterlyVersions: string[];
  slug: string;
  pageId: string | null;
  pricingMetrics?: any[]; // Optional pricing metrics (if not provided, uses Redux store)
}

interface Version {
  version: string;
  slug: string;
  page_id: string;
  selected: boolean;
}

interface PricingMetric {
  id: string;
  name?: string;
  display_name?: string;
  modality?: string;
}

const CURRENCY_OPTIONS = [
  { value: '$', label: '$ (USD)' },
  { value: '€', label: '€ (EUR)' },
  { value: '£', label: '£ (GBP)' },
  { value: '¥', label: '¥ (JPY)' },
  { value: '%', label: '% (Percentage)' }
];

const CHARGE_TYPE_OPTIONS = [
  { value: 'monthly_recurring_billed_yearly', label: 'Yearly billing' },
  { value: 'monthly_recurring_billed_monthly', label: 'Monthly billing' },
  { value: 'pricing_not_diclosed', label: 'Contact for pricing' },
  { value: 'usage', label: 'Usage-based' },
  { value: 'freemium', label: 'Free' }
];

const AddChargeModal: React.FC<AddChargeModalProps> = ({
  plan,
  isOpen,
  onClose,
  onSuccess,
  quarterlyVersions,
  slug,
  pageId,
  pricingMetrics
}) => {
  const { toast } = useToast();
  const [price, setPrice] = useState<string>('');
  const [currency, setCurrency] = useState<string>('$');
  const [pricePer, setPricePer] = useState<string>('');
  const [chargeType, setChargeType] = useState<string>('monthly_recurring_billed_monthly');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedMetricId, setSelectedMetricId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showMetricSearch, setShowMetricSearch] = useState(false);
  const [notes, setNotes] = useState<string>('');
  const [includedQuantity, setIncludedQuantity] = useState<string>('');
  const [overagePrice, setOveragePrice] = useState<string>('');
  const [overageQuantity, setOverageQuantity] = useState<string>('');
  const [minimumPrice, setMinimumPrice] = useState<string>('');
  const [versions, setVersions] = useState<Version[]>([]);
  const [isLoadingVersions, setIsLoadingVersions] = useState(false);
  const [fetchedPageId, setFetchedPageId] = useState<string | null>(null);
  
  // Get metrics from props (preferred) or Redux store (fallback)
  const reduxMetrics = useAppSelector(selectAllPricingMetrics);
  const allMetrics = pricingMetrics || reduxMetrics;
  
  // Debug logging
  React.useEffect(() => {
    if (isOpen) {
      console.log('🏗️ [AddChargeModal] Modal opened with metrics:', {
        fromProps: !!pricingMetrics,
        propsMetricsCount: pricingMetrics?.length || 0,
        reduxMetricsCount: reduxMetrics.length,
        finalMetricsCount: allMetrics.length,
        finalMetrics: allMetrics.map(m => ({ id: m.id, name: m.display_name, modality: m.modality }))
      });
    }
  }, [isOpen, pricingMetrics, reduxMetrics, allMetrics]);
  
  // Filter metrics to only include those with allowed modalities
  const allowedModalities = ['Flat', 'License', 'Credit', 'Usage'];
  const eligibleMetrics = allMetrics.filter((metric: any) => 
    metric.modality && allowedModalities.includes(metric.modality)
  );
  
  // Filter metrics based on search term
  const filteredMetrics = searchTerm.trim() === '' 
    ? eligibleMetrics 
    : eligibleMetrics.filter(metric => {
        const name = metric.display_name || metric.name || metric.id;
        return name.toLowerCase().includes(searchTerm.toLowerCase());
      });
      
  // Initialize versions array only when modal opens or any inputs change
  useEffect(() => {
    if (!isOpen) return;
    
    console.log('AddChargeModal initializing with props:', {
      quarterlyVersions,
      slug,
      pageId,
      pageIdType: pageId ? typeof pageId : 'null/undefined',
      hasPageId: !!pageId,
      planId: plan.id,
      planName: plan.display_name || plan.plan_name,
      planPageId: plan.page_id
    });
    
    // We should always have a pageId from the PlanRow component's fallback
    // But if we don't, try to fetch it directly from Supabase as a last resort
    let effectivePageId = pageId;
    
    if (!effectivePageId) {
      console.warn('Missing pageId in AddChargeModal - attempting to fetch from Supabase');
      
      // We'll use an async function inside this effect to fetch the page_id
      const fetchPageId = async () => {
        try {
          // Try to get the page_id using the URL slug if available
          const urlSlug = slug || ((window as any).slug as string) || '';
          if (urlSlug) {
            const { data, error } = await supabaseClient
              .from('page_data')
              .select('id')
              .eq('slug', urlSlug)
              .single();
              
            if (data && data.id) {
              console.log('Successfully retrieved page_id from Supabase:', data.id);
              effectivePageId = data.id;
              // Update state to trigger re-render with the new page_id
              setFetchedPageId(data.id);
              return;
            }
            
            if (error) {
              console.error('Error fetching page_id from page_data view:', error);
            }
          }
          
          // If we still don't have a page_id, try one more approach with plan.id
          if (plan.id) {
            const { data, error } = await supabaseClient
              .from('plans')
              .select('page_id')
              .eq('id', plan.id)
              .single();
              
            if (data && data.page_id) {
              console.log('Successfully retrieved page_id from plans table:', data.page_id);
              effectivePageId = data.page_id;
              // Update state to trigger re-render with the new page_id
              setFetchedPageId(data.page_id);
              return;
            }
            
            if (error) {
              console.error('Error fetching page_id from plans table:', error);
            }
          }
          
          console.error('Critical error: Failed to retrieve pageId for AddChargeModal versions after all attempts');
        } catch (err) {
          console.error('Exception when trying to fetch page_id:', err);
        }
      };
      
      // Execute the fetch function
      fetchPageId();
      
      // Don't return early yet, we'll use null as a temporary page_id and update it when the fetch completes
      console.warn('Proceeding with null page_id while attempting to fetch');
    }
    
    // Display the page_id for debugging
    console.log('Using pageId:', pageId);
    
    // Use the fetched page_id if available, otherwise use the prop
    const finalPageId = fetchedPageId || pageId;
    
    // Check if we have valid versions
    let versionObjects: Version[] = [];
    
    if (Array.isArray(quarterlyVersions) && quarterlyVersions.length > 0) {
      // We have valid quarterly versions from the PlansTable
      console.log('Using provided quarterly versions:', quarterlyVersions);
      
      // The versions in the debug output are already in the correct format
      versionObjects = quarterlyVersions.map(version => ({
        version,
        slug: slug || '', // Fallback to empty string if null/undefined
        page_id: finalPageId, // Use the fetched or passed pageId
        selected: true // Default to selected
      }));
    } else {
      // Fallback to dates in the format seen in the debug output
      // Using YYYYMMDD format instead of year-quarter format
      const today = new Date();
      const currentYear = today.getFullYear();
      const currentMonth = today.getMonth() + 1;
      
      // Create versions in the format "YYYYMMDD" to match what we saw in the debug output
      const fallbackVersions = [
        `${currentYear}0529`, // Current date format
        `${currentYear}0101`, // Beginning of year
        `${currentYear-1}1001`, // Previous year Q4
        `${currentYear-1}0701`, // Previous year Q3
        `${currentYear-1}0401`, // Previous year Q2
        `${currentYear-1}0101`  // Previous year Q1
      ];
      
      console.log('Using fallback date versions:', fallbackVersions);
      
      versionObjects = fallbackVersions.map(version => ({
        version,
        slug: slug || '', 
        page_id: finalPageId, // Use the fetched or passed pageId
        selected: true
      }));
    }
    
    // Set versions directly
    setVersions(versionObjects);
    
    // Log the final result
    console.log('AddChargeModal initialized with versions:', {
      versionCount: versionObjects.length,
      versions: versionObjects
    });
  }, [isOpen, quarterlyVersions, pageId, fetchedPageId, slug, plan.id, plan.display_name, plan.plan_name]);

  
  // Toggle version selection
  const toggleVersion = (version: string) => {
    setVersions(prevVersions => {
      return prevVersions.map(v => {
        if (v.version === version) {
          return { ...v, selected: !v.selected };
        }
        return v;
      });
    });
  };
  
  // Check if all versions are selected
  const allVersionsSelected = versions.length > 0 && versions.every(v => v.selected);
  
  // Toggle all versions
  const toggleAllVersions = () => {
    const newSelected = !allVersionsSelected;
    setVersions(prevVersions => {
      return prevVersions.map(v => ({ ...v, selected: newSelected }));
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Validation
    if (!pricePer?.trim()) {
      toast({
        title: "Validation Error",
        description: "Price Per is required (e.g., 'month', 'user', 'seat')",
        variant: "destructive",
      });
      setIsSubmitting(false);
      return;
    }
    
    if (!pageId && !fetchedPageId) {
      // We need a page ID to create the charge to associate it with the pricing page
      toast({
        title: "Missing Page ID",
        description: "Cannot create charge without page ID. Please specify a page ID or use a different method.",
        variant: "destructive",
      });
      setIsSubmitting(false);
      return;
    }
    
    const resolvedPageId = pageId || fetchedPageId;
    
    // If charge type is contact for pricing or free, set price to 0
    const finalPrice = ['pricing_not_diclosed', 'freemium'].includes(chargeType) 
      ? 0 
      : parseFloat(price || '0');
    
    try {
      // Create the charge input for GraphQL
      const chargeInput: ChargeInput = {
        price: finalPrice,
        currency,
        price_per: pricePer,
        type: chargeType,
        pricing_metric_id: selectedMetricId || null,
        plan_id: plan.id,
        notes,
        included_quantity: includedQuantity ? parseInt(includedQuantity) : null,
        overage_price: overagePrice || null,
        overage_quantity: overageQuantity ? parseInt(overageQuantity) : null,
        minimum_price: minimumPrice || null,
        versions: versions.filter(v => v.selected).map(v => v.version),
        page_id: resolvedPageId!
      };
      
      console.log('Submitting charge with GraphQL mutation:', chargeInput);
      
      // Call the GraphQL mutation
      const response = await addChargeMutation(chargeInput);
      
      if (!response || !response.addCharge) {
        throw new Error('Failed to create charge: No response from server');
      }
      
      const newCharge = response.addCharge;
      console.log('Created new charge:', newCharge);
      
      // Show success toast
      toast({
        title: "Charge Added",
        description: "The charge has been added successfully."
      });
      
      // Call the success callback with the new charge
      onSuccess(newCharge as unknown as Charge);
      
      // Close the modal
      onClose();
    } catch (error: any) {
      console.error('Error creating charge:', error);
      
      // Show error toast
      toast({
        title: "Error",
        description: `Failed to add charge: ${error.message || 'Unknown error'}`,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form when modal is closed
  const handleClose = () => {
    setPrice('');
    setCurrency('$');
    setPricePer('');
    setChargeType('monthly_recurring_billed_monthly');
    setSelectedMetricId(null);
    setSearchTerm('');
    setShowMetricSearch(false);
    setNotes('');
    setIncludedQuantity('');
    setOveragePrice('');
    setOverageQuantity('');
    setMinimumPrice('');
    setVersions([]);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => handleClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add Charge to {plan?.display_name || plan?.plan_name}</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <Label htmlFor="chargeType">Charge Type</Label>
            <select
              id="chargeType"
              value={chargeType}
              onChange={(e) => {
                setChargeType(e.target.value);
                // Reset price for certain charge types
                if (['pricing_not_diclosed', 'freemium'].includes(e.target.value)) {
                  setPrice('0');
                }
              }}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {CHARGE_TYPE_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <Label htmlFor="pricingMetric">Pricing Metric</Label>
            <div className="relative">
              <div 
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm cursor-pointer"
                onClick={() => setShowMetricSearch(!showMetricSearch)}
              >
                {selectedMetricId ? (
                  <div className="flex justify-between w-full items-center">
                    <span>{eligibleMetrics.find(m => m.id === selectedMetricId)?.display_name || selectedMetricId}</span>
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm" 
                      className="h-5 w-5 p-0" 
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedMetricId(null);
                      }}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <span className="text-gray-500">Select a pricing metric (optional)</span>
                )}
              </div>
              
              {showMetricSearch && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg">
                  <div className="p-2 border-b">
                    <div className="relative">
                      <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                      <Input
                        type="text"
                        placeholder="Search metrics..."
                        className="pl-8"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        autoFocus
                      />
                    </div>
                  </div>
                  
                  <div className="max-h-40 overflow-y-auto border rounded-md">
                    {filteredMetrics.length === 0 ? (
                      <div className="p-3 text-center text-gray-500 text-sm">
                        {searchTerm ? 'No matching metrics found' : 'No eligible metrics available'}
                      </div>
                    ) : (
                      <div>
                        <div 
                          className="p-2 border-b hover:bg-gray-50 cursor-pointer" 
                          onClick={() => {
                            setSelectedMetricId(null);
                            setShowMetricSearch(false);
                            setSearchTerm('');
                          }}>
                          <span className="text-gray-600">None (No metric association)</span>
                        </div>
                        {filteredMetrics.map(metric => (
                          <div 
                            key={metric.id} 
                            className="p-2 border-b last:border-b-0 hover:bg-gray-50 cursor-pointer"
                            onClick={() => {
                              setSelectedMetricId(metric.id);
                              setShowMetricSearch(false);
                              setSearchTerm('');
                            }}>
                            <div className="flex flex-col">
                              <span>{metric.display_name || metric.name || metric.id}</span>
                              <span className="text-xs text-gray-500">
                                Modality: {metric.modality} | ID: {metric.id}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-end gap-2">
            <div className="w-1/4">
              <Label htmlFor="currency">Currency</Label>
              <select
                id="currency"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {CURRENCY_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex-1">
              <Label htmlFor="price">Price</Label>
              <Input
                id="price"
                type="number"
                step="0.01"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="Enter price"
                min="0"
                disabled={['pricing_not_diclosed', 'freemium'].includes(chargeType)}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="pricePer">Price per <span className="text-red-500">*</span></Label>
            <Input
              id="pricePer"
              value={pricePer}
              onChange={(e) => setPricePer(e.target.value)}
              placeholder="e.g., month, user, request"
              required
              className={!pricePer?.trim() ? "border-red-300 focus:border-red-500" : ""}
            />
            {!pricePer?.trim() && (
              <p className="text-xs text-red-600">Price Per is required</p>
            )}
          </div>

          <div>
            <Label htmlFor="includedQuantity">Included Quantity (optional)</Label>
            <Input
              id="includedQuantity"
              type="number"
              value={includedQuantity}
              onChange={(e) => setIncludedQuantity(e.target.value)}
              placeholder="e.g., 1200"
            />
          </div>

          <div>
            <Label htmlFor="overagePrice">Overage Price (optional)</Label>
            <Input
              id="overagePrice"
              type="number"
              step="0.01"
              value={overagePrice}
              onChange={(e) => setOveragePrice(e.target.value)}
              placeholder="e.g., 0.08"
            />
          </div>

          <div>
            <Label htmlFor="overageQuantity">Overage Quantity (optional)</Label>
            <Input
              id="overageQuantity"
              type="number"
              value={overageQuantity}
              onChange={(e) => setOverageQuantity(e.target.value)}
              placeholder="e.g., 1000"
            />
          </div>

          <div>
            <Label htmlFor="minimumPrice">Minimum Price (optional)</Label>
            <Input
              id="minimumPrice"
              type="number"
              step="0.01"
              value={minimumPrice}
              onChange={(e) => setMinimumPrice(e.target.value)}
              placeholder="e.g., 10.00"
            />
          </div>

          <div>
            <Label htmlFor="notes">Notes (optional)</Label>
            <Input
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional notes about this charge"
            />
          </div>
          
          <div className="space-y-2">
            <div className="flex flex-col space-y-2 mb-2">
              <Label htmlFor="page-id">Page ID</Label>
              <div className="flex items-center">
                <div className={`px-3 py-1 ${(pageId || fetchedPageId) ? 'bg-gray-100' : 'bg-yellow-100'} border rounded-md text-sm font-mono truncate max-w-full`} 
                     title={(pageId || fetchedPageId) || 'Missing Page ID - Required for charge creation'}>
                  {(pageId || fetchedPageId) || 'MISSING PAGE ID - Attempting to fetch...'}
                </div>
              </div>
              {!(pageId || fetchedPageId) && (
                <div className="text-xs text-yellow-600 mt-1">
                  Warning: Page ID is required for creating charges. Check the page_data view in Supabase.
                </div>
              )}
            </div>
            
            <div className="flex items-center justify-between">
              <Label>Assign to versions</Label>
              <div className="flex items-center text-sm">
                <Checkbox
                  id="select-all-versions"
                  checked={allVersionsSelected}
                  onCheckedChange={toggleAllVersions}
                />
                <label
                  htmlFor="select-all-versions"
                  className="ml-2 text-sm cursor-pointer"
                >
                  Select all
                </label>
              </div>
            </div>
            
            <div className="border rounded-md p-2 max-h-40 overflow-y-auto">
              {isLoadingVersions ? (
                <div className="text-center py-2 text-gray-500">Loading versions...</div>
              ) : versions.length === 0 ? (
                <div className="text-center py-2 text-gray-500">No versions available</div>
              ) : (
                <div className="space-y-2">
                  {versions.map((v) => (
                    <div key={v.version} className="flex items-center space-x-2">
                      <Checkbox
                        id={`version-${v.version}`}
                        checked={v.selected}
                        onCheckedChange={() => toggleVersion(v.version)}
                      />
                      <label
                        htmlFor={`version-${v.version}`}
                        className="text-sm cursor-pointer flex-1"
                      >
                        {v.version}
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {['pricing_not_diclosed', 'freemium'].includes(chargeType) && (
            <Alert className="bg-blue-50 border-blue-200">
              <AlertCircle className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-700">
                Price will be automatically set to 0 for {chargeType === 'freemium' ? 'free' : 'contact pricing'} charges.
              </AlertDescription>
            </Alert>
          )}

          <DialogFooter className="pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Adding...' : 'Add Charge'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddChargeModal;
